import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSocial } from '../hooks/useSocial';
import { Plus, Inbox, ArrowLeftRight } from 'lucide-react';
import { formatCurrency } from '../utils/portfolioValue';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

const STATUS_STYLES = {
  proposed: 'bg-yellow/15 border-yellow/40 text-yellow-dark',
  accepted: 'bg-success-bg border-success-border text-success-green',
  declined: 'bg-danger-bg border-danger-border text-brick',
  cancelled: 'bg-cream-bg border-border text-ink-muted',
  completed: 'bg-navy-dark border-navy-dark text-white'
};

const ItemLine = ({ item }) => (
  <div className="flex items-center gap-2">
    <img
      src={item.cardImage || FALLBACK_IMG}
      alt={item.cardName}
      className="w-7 h-9 object-contain rounded flex-shrink-0"
      onError={(e) => {
        e.target.src = FALLBACK_IMG;
      }}
    />
    <div className="min-w-0 flex-1">
      <span className="text-[12px] font-medium text-ink-primary">
        {item.quantity > 1 ? `${item.quantity}× ` : ''}
        {item.cardName || item.cardId}
      </span>
      <span className="text-[11px] text-ink-secondary">
        {' '}
        ({item.variant}
        {item.setName ? ` • ${item.setName} #${item.cardNumber}` : ''})
      </span>
    </div>
    {item.unitPrice > 0 && (
      <span className="text-[10px] text-ink-muted flex-shrink-0">
        ≈ {formatCurrency(item.unitPrice * item.quantity)}
      </span>
    )}
  </div>
);

const TradeCard = ({ trade, myUserId, onAction, busy }) => {
  const iAmProposer = trade.proposerId === myUserId;
  const otherName = iAmProposer ? trade.recipientUsername : trade.proposerUsername;
  const iGive = trade.items.filter(
    (i) => i.direction === (iAmProposer ? 'proposer_gives' : 'recipient_gives')
  );
  const iGet = trade.items.filter(
    (i) => i.direction === (iAmProposer ? 'recipient_gives' : 'proposer_gives')
  );

  const [confirmComplete, setConfirmComplete] = useState(false);

  return (
    <div className="bg-white border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-ink-primary flex items-center gap-1.5">
          <ArrowLeftRight size={14} className="text-navy-dark" />
          {iAmProposer ? `To ${otherName}` : `From ${otherName}`}
        </div>
        <span
          className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${
            STATUS_STYLES[trade.status]
          }`}
        >
          {trade.status}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div className="bg-cream-bg border border-border rounded-xl p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-yellow-dark mb-1.5">
            You give
          </div>
          <div className="space-y-1.5">
            {iGive.length === 0 ? (
              <span className="text-[11px] text-ink-muted">Nothing</span>
            ) : (
              iGive.map((item, idx) => <ItemLine key={idx} item={item} />)
            )}
          </div>
        </div>
        <div className="bg-cream-bg border border-border rounded-xl p-2.5">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-success-green mb-1.5">
            You get
          </div>
          <div className="space-y-1.5">
            {iGet.length === 0 ? (
              <span className="text-[11px] text-ink-muted">Nothing</span>
            ) : (
              iGet.map((item, idx) => <ItemLine key={idx} item={item} />)
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] text-ink-muted">
          {new Date(trade.updatedAt || trade.createdAt).toLocaleDateString()}
        </span>

        <div className="flex items-center gap-2">
          {trade.status === 'proposed' && !iAmProposer && (
            <>
              <button
                onClick={() => onAction(trade.id, 'decline')}
                disabled={busy}
                className="text-[12px] font-semibold text-brick bg-danger-bg border border-danger-border px-3 py-1.5 rounded-lg hover:bg-danger-border transition-colors"
              >
                Decline
              </button>
              <button
                onClick={() => onAction(trade.id, 'accept')}
                disabled={busy}
                className="text-[12px] font-semibold text-success-green bg-success-bg border border-success-border px-3 py-1.5 rounded-lg hover:bg-success-border transition-colors"
              >
                Accept
              </button>
            </>
          )}
          {trade.status === 'proposed' && iAmProposer && (
            <button
              onClick={() => onAction(trade.id, 'cancel')}
              disabled={busy}
              className="text-[12px] font-semibold text-ink-secondary bg-cream-bg border border-border px-3 py-1.5 rounded-lg hover:text-brick transition-colors"
            >
              Cancel
            </button>
          )}
          {trade.status === 'accepted' &&
            (confirmComplete ? (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-ink-secondary max-w-[180px] text-right">
                  Both collections will be updated
                </span>
                <button
                  onClick={() => onAction(trade.id, 'complete')}
                  disabled={busy}
                  className="text-[12px] font-semibold text-white bg-navy-dark px-3 py-1.5 rounded-lg hover:bg-navy-light transition-colors"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmComplete(false)}
                  className="text-[12px] text-ink-secondary px-1"
                >
                  Not yet
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => onAction(trade.id, 'cancel')}
                  disabled={busy}
                  className="text-[12px] font-semibold text-ink-secondary bg-cream-bg border border-border px-3 py-1.5 rounded-lg hover:text-brick transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setConfirmComplete(true)}
                  disabled={busy}
                  className="text-[12px] font-semibold text-white bg-navy-dark px-3 py-1.5 rounded-lg hover:bg-navy-light transition-colors"
                >
                  Mark Completed
                </button>
              </>
            ))}
        </div>
      </div>
    </div>
  );
};

const ProposalsTab = () => {
  const { user } = useAuth();
  const { trades, tradesHistory, tradesLoaded, actOnTrade, openProposalBuilder, friends } = useSocial();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const handleAction = async (tradeId, action) => {
    setError('');
    setBusy(true);
    try {
      await actOnTrade(tradeId, action);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!tradesLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-navy-dark border-t-transparent"></div>
      </div>
    );
  }

  const list = showHistory ? tradesHistory : trades;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 bg-cream-card border border-border rounded-lg p-0.5">
          <button
            onClick={() => setShowHistory(false)}
            className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
              !showHistory ? 'bg-white text-ink-primary shadow-sm' : 'text-ink-secondary'
            }`}
          >
            Active ({trades.length})
          </button>
          <button
            onClick={() => setShowHistory(true)}
            className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-colors ${
              showHistory ? 'bg-white text-ink-primary shadow-sm' : 'text-ink-secondary'
            }`}
          >
            History ({tradesHistory.length})
          </button>
        </div>

        <button
          onClick={() => openProposalBuilder({})}
          disabled={friends.length === 0}
          className={`flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-2 rounded-lg transition-colors ${
            friends.length === 0
              ? 'bg-border text-ink-faint cursor-not-allowed'
              : 'bg-navy-dark text-yellow hover:bg-navy-light'
          }`}
        >
          <Plus size={13} />
          New Proposal
        </button>
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 bg-danger-bg border border-danger-border rounded-xl text-brick text-[13px]">
          {error}
        </div>
      )}

      {list.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center bg-white border border-border rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-cream-bg border border-border flex items-center justify-center mb-3">
            <Inbox size={20} className="text-ink-faint" />
          </div>
          <p className="text-[13px] text-ink-secondary max-w-xs">
            {showHistory
              ? 'No completed, declined, or cancelled trades yet.'
              : friends.length === 0
                ? 'Add friends first — then propose trades from their matches.'
                : 'No active proposals. Start one from a match or with New Proposal.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((t) => (
            <TradeCard key={t.id} trade={t} myUserId={user.id} onAction={handleAction} busy={busy} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProposalsTab;
